import LockIcon from "@mui/icons-material/Lock";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";

import useAuth from "../../hooks/useAuth";
import {
  internalFeatureLabels,
  isInternalFeatureEnabled,
  userCanAccessInternalFeature,
  type InternalFeature,
} from "../featureFlags";

import type { ReactNode } from "react";

interface FeatureRouteGuardProps {
  children: ReactNode;
  feature: InternalFeature;
}

const FeatureAccessDenied = ({ feature }: { feature: InternalFeature }) => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        minHeight: "calc(100vh - 96px)",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, maxWidth: 460, p: 3 }}>
        <Stack alignItems="center" spacing={2} textAlign="center">
          <Box
            sx={{
              alignItems: "center",
              bgcolor: "action.hover",
              borderRadius: "50%",
              display: "flex",
              height: 56,
              justifyContent: "center",
              width: 56,
            }}
          >
            <LockIcon color="primary" />
          </Box>
          <Box>
            <Typography fontWeight={900} variant="h5">
              Access restricted
            </Typography>
            <Typography color="text.secondary" mt={1} variant="body2">
              {internalFeatureLabels[feature]} is available only to internal roles configured for preview features.
            </Typography>
          </Box>
          <Button onClick={() => navigate("/voyages", { replace: true })} variant="contained">
            Back to clients
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

const FeatureRouteGuard = ({ children, feature }: FeatureRouteGuardProps) => {
  const { user } = useAuth();

  if (!isInternalFeatureEnabled(feature)) {
    return <Navigate replace to="/404" />;
  }

  if (!userCanAccessInternalFeature(user)) {
    return <FeatureAccessDenied feature={feature} />;
  }

  return <>{children}</>;
};

export default FeatureRouteGuard;
